/**
 * QA Dashboard Page
 * AIDLC Administrative Backend
 *
 * Provides full transparency into Asha's QA work:
 * - Test summary statistics (files, tests, assertions, effectiveness)
 * - Anti-pattern audit with file:line:code evidence
 * - Use case library (rendered markdown)
 * - Playwright report viewer (iframe)
 * - Governance rule gov-rule-009 compliance status
 *
 * CEO directive: "Overwhelm me with details. Asha needs to be transparent."
 */

import { useState, useEffect, useCallback } from 'react';
import apiClient from '../../services/api';

// ─── Types ────────────────────────────────────────────────────────

interface SpecFile {
  file: string;
  lines: number;
  tests: number;
  assertions: number;
}

interface TestSummary {
  available: boolean;
  specFiles: SpecFile[];
  totalFiles: number;
  totalTests: number;
  totalAssertions: number;
  totalLines: number;
  playwrightReportExists: boolean;
  message?: string;
}

interface AntiPatternFinding {
  id: number;
  file: string;
  line: number;
  badCode: string;
  explanation: string;
  fixedCode: string;
  severity: string;
  pattern: string;
}

interface AntiPatternAudit {
  available: boolean;
  reportDate?: string;
  author?: string;
  totalFiles?: number;
  affectedFiles?: number;
  totalBadAssertions?: number;
  totalGoodAssertions?: number;
  findings?: AntiPatternFinding[];
  message?: string;
}

interface UseCaseFile {
  filename: string;
  title: string;
  content: string;
  sizeBytes: number;
}

// ─── Tab definitions ──────────────────────────────────────────────

type TabId = 'overview' | 'anti-pattern' | 'use-cases' | 'playwright' | 'evidence';

const TABS: { id: TabId; label: string; icon: string }[] = [
  { id: 'overview', label: 'Overview', icon: '📊' },
  { id: 'anti-pattern', label: 'Anti-Pattern Audit', icon: '🔴' },
  { id: 'use-cases', label: 'Use Case Library', icon: '📋' },
  { id: 'playwright', label: 'Playwright Report', icon: '🎭' },
  { id: 'evidence', label: 'Evidence & Videos', icon: '🎬' },
];

// ─── Stat Card ────────────────────────────────────────────────────

function StatCard({ label, value, subtext, color }: {
  label: string; value: string | number; subtext?: string; color: 'blue' | 'red' | 'green' | 'amber' | 'cyan';
}) {
  const colorMap = {
    blue: 'border-blue-500 bg-blue-500/10 text-blue-400',
    red: 'border-red-500 bg-red-500/10 text-red-400',
    green: 'border-green-500 bg-green-500/10 text-green-400',
    amber: 'border-amber-500 bg-amber-500/10 text-amber-400',
    cyan: 'border-cyan-500 bg-cyan-500/10 text-cyan-400',
  };

  return (
    <div className={`rounded-lg border-l-4 p-4 ${colorMap[color]}`}>
      <div className="text-2xl font-bold">{value}</div>
      <div className="text-sm font-medium mt-1">{label}</div>
      {subtext && <div className="text-xs opacity-60 mt-1">{subtext}</div>}
    </div>
  );
}

// ─── Overview Tab ─────────────────────────────────────────────────

function OverviewTab({ summary, audit }: { summary: TestSummary | null; audit: AntiPatternAudit | null }) {
  if (!summary?.available) {
    return <div className="text-white/50 p-8 text-center">Loading test data...</div>;
  }

  const badCount = audit?.totalBadAssertions ?? 0;
  const goodCount = audit?.totalGoodAssertions ?? 0;
  const total = badCount + goodCount;
  const effectiveness = total > 0 ? Math.round((goodCount / total) * 100) : 0;

  return (
    <div className="space-y-6">
      {/* Stat Grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
        <StatCard label="Spec Files" value={summary.totalFiles} subtext="Playwright test files" color="blue" />
        <StatCard label="Test Cases" value={summary.totalTests} subtext="Individual tests" color="cyan" />
        <StatCard label="Assertions" value={summary.totalAssertions} subtext="Total expect() calls" color="blue" />
        <StatCard label="Lines of Test Code" value={summary.totalLines.toLocaleString()} color="cyan" />
        <StatCard
          label="Bad Assertions"
          value={badCount}
          subtext="Can never fail (tautologies)"
          color={badCount > 0 ? 'red' : 'green'}
        />
        <StatCard
          label="Effectiveness"
          value={`${effectiveness}%`}
          subtext={`${goodCount} real / ${total} total`}
          color={effectiveness >= 90 ? 'green' : effectiveness >= 50 ? 'amber' : 'red'}
        />
      </div>

      {/* Governance Compliance */}
      <div className="bg-gray-900 rounded-lg border border-white/10 p-6">
        <h3 className="text-lg font-semibold text-white mb-4">Governance Compliance — gov-rule-009</h3>
        <div className="space-y-3">
          <ComplianceRow
            label="Evidence-based reporting (file:line:code)"
            status={audit?.available ? 'pass' : 'pending'}
          />
          <ComplianceRow
            label="Anti-pattern audit completed"
            status={audit?.available ? 'pass' : 'fail'}
          />
          <ComplianceRow
            label="Playwright video recording enabled"
            status="pass"
            detail="video: 'on' in playwright.config.ts"
          />
          <ComplianceRow
            label="QA Dashboard published"
            status="pass"
            detail="You're looking at it"
          />
          <ComplianceRow
            label="All tautology assertions eliminated"
            status={badCount === 0 ? 'pass' : 'fail'}
            detail={badCount > 0 ? `${badCount} tautologies remain — tracked in backlog` : 'All assertions can fail'}
          />
          <ComplianceRow
            label="Use case library mapped to tests"
            status={summary.totalFiles >= 35 ? 'pass' : 'fail'}
            detail={`${summary.totalFiles}/35 spec files exist`}
          />
        </div>
      </div>

      {/* Per-File Breakdown */}
      <div className="bg-gray-900 rounded-lg border border-white/10 overflow-hidden">
        <div className="px-6 py-4 border-b border-white/10">
          <h3 className="text-lg font-semibold text-white">Per-File Test Breakdown</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-white/40 border-b border-white/10">
                <th className="px-4 py-3 font-medium">File</th>
                <th className="px-4 py-3 font-medium text-right">Lines</th>
                <th className="px-4 py-3 font-medium text-right">Tests</th>
                <th className="px-4 py-3 font-medium text-right">Assertions</th>
              </tr>
            </thead>
            <tbody>
              {summary.specFiles.map((f, i) => (
                <tr key={f.file} className={`border-b border-white/5 ${i % 2 === 0 ? 'bg-white/[0.02]' : ''}`}>
                  <td className="px-4 py-2 text-white/80 font-mono text-xs">{f.file}</td>
                  <td className="px-4 py-2 text-white/60 text-right">{f.lines}</td>
                  <td className="px-4 py-2 text-white/60 text-right">{f.tests}</td>
                  <td className="px-4 py-2 text-white/60 text-right">{f.assertions}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function ComplianceRow({ label, status, detail }: { label: string; status: 'pass' | 'fail' | 'pending'; detail?: string }) {
  const icon = status === 'pass' ? '✅' : status === 'fail' ? '❌' : '⏳';
  const color = status === 'pass' ? 'text-green-400' : status === 'fail' ? 'text-red-400' : 'text-amber-400';

  return (
    <div className="flex items-start gap-3">
      <span className="text-lg flex-shrink-0">{icon}</span>
      <div>
        <div className={`text-sm font-medium ${color}`}>{label}</div>
        {detail && <div className="text-xs text-white/40 mt-0.5">{detail}</div>}
      </div>
    </div>
  );
}

// ─── Anti-Pattern Audit Tab ───────────────────────────────────────

function AntiPatternTab({ audit }: { audit: AntiPatternAudit | null }) {
  const [showReport, setShowReport] = useState(false);

  if (!audit?.available || !audit.findings) {
    return (
      <div className="text-white/50 p-8 text-center">
        <p className="text-lg mb-2">Anti-pattern audit not yet generated</p>
        <p className="text-sm">Asha is working on the transparency report...</p>
      </div>
    );
  }

  const bySeverity = audit.findings.reduce((acc, f) => {
    acc[f.severity] = (acc[f.severity] || 0) + 1;
    return acc;
  }, {} as Record<string, number>);

  const byPattern = audit.findings.reduce((acc, f) => {
    acc[f.pattern] = (acc[f.pattern] || 0) + 1;
    return acc;
  }, {} as Record<string, number>);

  const byFile = audit.findings.reduce((acc, f) => {
    acc[f.file] = (acc[f.file] || 0) + 1;
    return acc;
  }, {} as Record<string, number>);

  return (
    <div className="space-y-6">
      {/* Summary Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard label="Affected Files" value={audit.affectedFiles ?? 0} subtext={`of ${audit.totalFiles} total`} color="red" />
        <StatCard label="Bad Assertions" value={audit.totalBadAssertions ?? 0} color="red" />
        <StatCard label="Good Assertions" value={audit.totalGoodAssertions ?? 0} color="green" />
        <StatCard
          label="Fix Rate"
          value="0%"
          subtext="Backlog item created — pending fix"
          color="red"
        />
      </div>

      {/* View full HTML report button */}
      <div className="flex gap-4">
        <button
          onClick={() => setShowReport(!showReport)}
          className="px-4 py-2 bg-ldaja-blue text-white rounded-lg hover:bg-blue-600 transition-colors text-sm font-medium"
        >
          {showReport ? 'Hide' : 'View'} Full HTML Audit Report
        </button>
      </div>

      {showReport && (
        <div className="bg-gray-900 rounded-lg border border-white/10 overflow-hidden">
          <iframe
            src="/api/qa/anti-pattern-report"
            className="w-full border-0"
            style={{ height: '80vh' }}
            title="Anti-Pattern Audit Report"
          />
        </div>
      )}

      {/* Breakdown by Pattern */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="bg-gray-900 rounded-lg border border-white/10 p-6">
          <h3 className="text-base font-semibold text-white mb-4">By Pattern Type</h3>
          {Object.entries(byPattern).sort((a, b) => b[1] - a[1]).map(([pattern, count]) => (
            <div key={pattern} className="flex justify-between items-center py-2 border-b border-white/5">
              <span className="text-sm text-white/70 font-mono">{pattern}</span>
              <span className="text-sm font-bold text-red-400">{count}</span>
            </div>
          ))}
        </div>

        <div className="bg-gray-900 rounded-lg border border-white/10 p-6">
          <h3 className="text-base font-semibold text-white mb-4">By Severity</h3>
          {Object.entries(bySeverity).sort((a, b) => b[1] - a[1]).map(([severity, count]) => (
            <div key={severity} className="flex justify-between items-center py-2 border-b border-white/5">
              <span className={`text-sm font-medium ${severity === 'CRITICAL' ? 'text-red-400' : severity === 'MAJOR' ? 'text-amber-400' : 'text-white/70'}`}>
                {severity}
              </span>
              <span className="text-sm font-bold text-white/80">{count}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Worst Offenders */}
      <div className="bg-gray-900 rounded-lg border border-white/10 p-6">
        <h3 className="text-base font-semibold text-white mb-4">Worst Offending Files</h3>
        {Object.entries(byFile).sort((a, b) => b[1] - a[1]).map(([file, count]) => (
          <div key={file} className="flex justify-between items-center py-2 border-b border-white/5">
            <span className="text-xs text-white/70 font-mono">{file}</span>
            <span className={`text-sm font-bold ${count >= 5 ? 'text-red-400' : count >= 3 ? 'text-amber-400' : 'text-white/60'}`}>
              {count} issues
            </span>
          </div>
        ))}
      </div>

      {/* Full Findings Table */}
      <div className="bg-gray-900 rounded-lg border border-white/10 overflow-hidden">
        <div className="px-6 py-4 border-b border-white/10">
          <h3 className="text-lg font-semibold text-white">
            All Findings ({audit.findings.length} total)
          </h3>
          <p className="text-xs text-white/40 mt-1">Every instance of tautological assertion with file, line, and exact code</p>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="text-left text-white/40 border-b border-white/10">
                <th className="px-3 py-2 font-medium w-8">#</th>
                <th className="px-3 py-2 font-medium">File</th>
                <th className="px-3 py-2 font-medium w-12">Line</th>
                <th className="px-3 py-2 font-medium">Bad Code</th>
                <th className="px-3 py-2 font-medium">Fix</th>
                <th className="px-3 py-2 font-medium w-20">Severity</th>
              </tr>
            </thead>
            <tbody>
              {audit.findings.map((f) => (
                <tr key={f.id} className="border-b border-white/5 hover:bg-white/[0.03]">
                  <td className="px-3 py-2 text-white/40">{f.id}</td>
                  <td className="px-3 py-2 font-mono text-white/70">{f.file}</td>
                  <td className="px-3 py-2 text-white/60 text-center">{f.line}</td>
                  <td className="px-3 py-2">
                    <code className="bg-red-500/20 text-red-300 px-1.5 py-0.5 rounded text-[11px] break-all">
                      {f.badCode}
                    </code>
                  </td>
                  <td className="px-3 py-2">
                    <code className="bg-green-500/20 text-green-300 px-1.5 py-0.5 rounded text-[11px] break-all">
                      {f.fixedCode}
                    </code>
                  </td>
                  <td className="px-3 py-2">
                    <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${
                      f.severity === 'CRITICAL' ? 'bg-red-500/20 text-red-400' :
                      f.severity === 'MAJOR' ? 'bg-amber-500/20 text-amber-400' :
                      'bg-white/10 text-white/60'
                    }`}>
                      {f.severity}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// ─── Use Cases Tab ────────────────────────────────────────────────

function UseCasesTab({ useCases }: { useCases: UseCaseFile[] }) {
  const [selected, setSelected] = useState<string | null>(null);

  if (useCases.length === 0) {
    return <div className="text-white/50 p-8 text-center">No use case files found</div>;
  }

  const activeFile = useCases.find(f => f.filename === selected);

  return (
    <div className="flex gap-6">
      {/* File List */}
      <div className="w-64 flex-shrink-0 space-y-1">
        {useCases.map(f => (
          <button
            key={f.filename}
            onClick={() => setSelected(f.filename)}
            className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors ${
              selected === f.filename
                ? 'bg-ldaja-blue/20 text-white border border-ldaja-blue/30'
                : 'text-white/60 hover:text-white hover:bg-white/5'
            }`}
          >
            <div className="font-medium capitalize">{f.title}</div>
            <div className="text-[10px] text-white/30 mt-0.5">{f.filename} ({Math.round(f.sizeBytes / 1024)}KB)</div>
          </button>
        ))}
      </div>

      {/* Content */}
      <div className="flex-1 bg-gray-900 rounded-lg border border-white/10 overflow-hidden">
        {activeFile ? (
          <div className="p-6 overflow-y-auto max-h-[70vh]">
            <pre className="text-xs text-white/80 whitespace-pre-wrap font-mono leading-relaxed">{activeFile.content}</pre>
          </div>
        ) : (
          <div className="p-8 text-center text-white/40">
            Select a use case file to view its contents
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Playwright Report Tab ────────────────────────────────────────

function PlaywrightTab({ reportExists }: { reportExists: boolean }) {
  if (!reportExists) {
    return (
      <div className="bg-gray-900 rounded-lg border border-white/10 p-8 text-center">
        <p className="text-lg text-white/60 mb-4">Playwright HTML Report Not Yet Generated</p>
        <p className="text-sm text-white/40 mb-6">Run the test suite to generate the report:</p>
        <code className="bg-black px-4 py-2 rounded text-cyan-400 text-sm font-mono">npm run test:e2e</code>
        <p className="text-xs text-white/30 mt-4">
          After running, the report will be available at <code>playwright-report/index.html</code>
        </p>
      </div>
    );
  }

  return (
    <div className="bg-gray-900 rounded-lg border border-white/10 overflow-hidden">
      <div className="px-6 py-4 border-b border-white/10 flex items-center justify-between">
        <h3 className="text-base font-semibold text-white">Playwright Test Report</h3>
        <a
          href="/api/qa/playwright-report"
          target="_blank"
          rel="noopener noreferrer"
          className="text-xs text-ldaja-cyan hover:text-cyan-300 transition-colors"
        >
          Open in new tab ↗
        </a>
      </div>
      <iframe
        src="/api/qa/playwright-report"
        className="w-full border-0"
        style={{ height: '80vh' }}
        title="Playwright Test Report"
      />
    </div>
  );
}

// ─── Evidence Tab ─────────────────────────────────────────────────

function EvidenceTab() {
  return (
    <div className="space-y-6">
      <div className="bg-gray-900 rounded-lg border border-white/10 p-6">
        <h3 className="text-base font-semibold text-white mb-4">QA Evidence Directory</h3>
        <p className="text-sm text-white/60 mb-4">
          All QA evidence files are stored in <code className="text-cyan-400">docs/qa-evidence/</code>
        </p>

        <div className="space-y-3">
          <div className="flex items-center gap-3 p-3 bg-white/5 rounded-lg">
            <span className="text-lg">📄</span>
            <div>
              <div className="text-sm text-white font-medium">anti-pattern-audit.html</div>
              <div className="text-xs text-white/40">Full HTML report with all findings, styled for review</div>
            </div>
            <a
              href="/api/qa/anti-pattern-report"
              target="_blank"
              rel="noopener noreferrer"
              className="ml-auto text-xs text-ldaja-cyan hover:text-cyan-300"
            >
              View ↗
            </a>
          </div>

          <div className="flex items-center gap-3 p-3 bg-white/5 rounded-lg">
            <span className="text-lg">📊</span>
            <div>
              <div className="text-sm text-white font-medium">anti-pattern-findings.json</div>
              <div className="text-xs text-white/40">Machine-readable findings data for dashboards</div>
            </div>
          </div>

          <div className="flex items-center gap-3 p-3 bg-white/5 rounded-lg">
            <span className="text-lg">🎬</span>
            <div>
              <div className="text-sm text-white font-medium">Playwright Test Videos</div>
              <div className="text-xs text-white/40">Generated after running <code>npm run test:e2e</code> — stored in test-results/</div>
            </div>
          </div>
        </div>
      </div>

      <div className="bg-gray-900 rounded-lg border border-amber-500/30 p-6">
        <h3 className="text-base font-semibold text-amber-400 mb-2">Note on Playwright Videos</h3>
        <p className="text-sm text-white/60">
          Playwright records videos in WebM format during test execution. To view them:
        </p>
        <ol className="text-sm text-white/60 mt-2 space-y-1 list-decimal list-inside">
          <li>Run tests: <code className="text-cyan-400">npm run test:e2e</code></li>
          <li>Open report: <code className="text-cyan-400">npm run test:e2e:report</code></li>
          <li>Click any test to see its video, trace, and screenshots</li>
        </ol>
        <p className="text-xs text-white/40 mt-3">
          The Playwright HTML report viewer handles video playback natively — no conversion needed.
        </p>
      </div>
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────────

export function QaDashboardPage() {
  const [activeTab, setActiveTab] = useState<TabId>('overview');
  const [summary, setSummary] = useState<TestSummary | null>(null);
  const [audit, setAudit] = useState<AntiPatternAudit | null>(null);
  const [useCases, setUseCases] = useState<UseCaseFile[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);

    const [summaryRes, auditRes, useCasesRes] = await Promise.allSettled([
      apiClient.get<TestSummary>('/qa/test-summary'),
      apiClient.get<AntiPatternAudit>('/qa/anti-pattern-audit'),
      apiClient.get<{ files: UseCaseFile[] }>('/qa/use-cases'),
    ]);

    if (summaryRes.status === 'fulfilled') setSummary(summaryRes.value.data);
    if (auditRes.status === 'fulfilled') setAudit(auditRes.value.data);
    if (useCasesRes.status === 'fulfilled') setUseCases(useCasesRes.value.data.files);

    if (summaryRes.status === 'rejected' && auditRes.status === 'rejected' && useCasesRes.status === 'rejected') {
      setError('Failed to load QA data. Check if the backend is running.');
    }

    setLoading(false);
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">QA Dashboard</h1>
          <p className="text-sm text-white/50 mt-1">
            Asha Patel — QA Transparency & Accountability | gov-rule-009
          </p>
        </div>
        <button
          onClick={loadData}
          disabled={loading}
          className="px-4 py-2 bg-white/5 hover:bg-white/10 border border-white/10 rounded-lg text-sm text-white/70 hover:text-white transition-colors disabled:opacity-50"
        >
          {loading ? 'Loading...' : 'Refresh'}
        </button>
      </div>

      {error && (
        <div className="bg-red-500/10 border border-red-500/30 rounded-lg px-4 py-3 text-sm text-red-400">
          {error}
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-1 bg-gray-900 rounded-lg p-1 border border-white/10">
        {TABS.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              activeTab === tab.id
                ? 'bg-ldaja-blue text-white'
                : 'text-white/50 hover:text-white hover:bg-white/5'
            }`}
          >
            <span>{tab.icon}</span>
            <span>{tab.label}</span>
          </button>
        ))}
      </div>

      {/* Tab Content */}
      {activeTab === 'overview' && <OverviewTab summary={summary} audit={audit} />}
      {activeTab === 'anti-pattern' && <AntiPatternTab audit={audit} />}
      {activeTab === 'use-cases' && <UseCasesTab useCases={useCases} />}
      {activeTab === 'playwright' && <PlaywrightTab reportExists={summary?.playwrightReportExists ?? false} />}
      {activeTab === 'evidence' && <EvidenceTab />}
    </div>
  );
}
